import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Container, Paper, Typography, Button, Alert, CircularProgress, Box, Stack, Link } from '@mui/material';
import { businessService } from '../services/businessService';

// The page behind the link in the "is your business still operating?" email.
// Opening it changes nothing - the business answers with one of the two buttons.

const faDate = (d: string) => {
    try { return new Date(d).toLocaleDateString('fa-IR', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return ''; }
};
const enDate = (d: string) => new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

const ListingCheckPage: React.FC = () => {
    const { token = '' } = useParams<{ token: string }>();
    const [info, setInfo] = useState<any | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [state, setState] = useState<'pending' | 'confirmed' | 'removed' | null>(null);
    const [askClosed, setAskClosed] = useState(false);

    useEffect(() => {
        businessService.getListingCheck(token)
            .then(d => { setInfo(d); setState(d.state); })
            .catch(err => setError(err?.response?.data?.error || 'This link is no longer valid.'));
    }, [token]);

    const answer = async (a: 'active' | 'closed') => {
        setBusy(true);
        setError(null);
        try {
            const r = await businessService.answerListingCheck(token, a);
            setState(r.state);
        } catch (err: any) {
            setError(err?.response?.data?.error || 'Something went wrong - please try again.');
        } finally {
            setBusy(false);
        }
    };

    const listingPath = info ? `/business/${encodeURIComponent(info.slug)}` : '/';

    return (
        <Container maxWidth="sm" sx={{ my: 6 }}>
            <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
                {!info && !error && <Box sx={{ textAlign: 'center' }}><CircularProgress /></Box>}
                {error && !info && <Alert severity="error">{error}</Alert>}

                {info && (
                    <>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>{info.name}</Typography>

                        {state === 'pending' && !askClosed && (
                            <>
                                <Typography dir="rtl" sx={{ mb: 1 }}>
                                    آیا این کسب و کار هنوز فعال است؟ {info.deadline && <>لطفاً تا <strong>{faDate(info.deadline)}</strong> پاسخ دهید، در غیر این صورت آگهی از چیستا حذف می‌شود.</>}
                                </Typography>
                                <Typography sx={{ mb: 3 }}>
                                    Is this business still operating? {info.deadline && <>Please answer by <strong>{enDate(info.deadline)}</strong>, otherwise the listing will be removed from Cheesta.</>}
                                </Typography>
                                <Stack spacing={1.5}>
                                    <Button variant="contained" size="large" disabled={busy} onClick={() => answer('active')}>
                                        بله، فعال هستیم – Yes, we're still operating
                                    </Button>
                                    <Button color="inherit" disabled={busy} onClick={() => setAskClosed(true)}>
                                        کسب و کار ما بسته شده است – We have closed
                                    </Button>
                                </Stack>
                            </>
                        )}

                        {state === 'pending' && askClosed && (
                            <>
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    <div dir="rtl">آگهی شما همین حالا از چیستا حذف می‌شود. مطمئن هستید؟</div>
                                    Your listing will be removed from Cheesta now. Are you sure?
                                </Alert>
                                <Stack direction="row" spacing={1.5} justifyContent="center">
                                    <Button variant="contained" color="error" disabled={busy} onClick={() => answer('closed')}>بله، حذف شود – Yes, remove it</Button>
                                    <Button disabled={busy} onClick={() => setAskClosed(false)}>بازگشت – Go back</Button>
                                </Stack>
                            </>
                        )}

                        {state === 'confirmed' && (
                            <>
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    <div dir="rtl">ممنون! آگهی شما در چیستا باقی می‌ماند.</div>
                                    Thank you! Your listing stays on Cheesta.
                                </Alert>
                                <Typography dir="rtl" variant="body2" sx={{ mb: 0.5 }}>می‌توانید وارد شوید و اطلاعات، عکس‌ها و ساعات کاری آگهی را به‌روزرسانی کنید.</Typography>
                                <Typography variant="body2" sx={{ mb: 2 }}>You're welcome to log in and update its details, photos and opening hours.</Typography>
                                <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                                    <Button variant="contained" component={RouterLink} to={listingPath}>مشاهده آگهی – View listing</Button>
                                    {info.updateUrl && <Button variant="outlined" href={info.updateUrl}>ورود و به‌روزرسانی – Log in and update</Button>}
                                </Stack>
                            </>
                        )}

                        {state === 'removed' && (
                            <Alert severity="info">
                                <div dir="rtl">این آگهی از چیستا حذف شده است. اگر اشتباهی رخ داده، با ما تماس بگیرید.</div>
                                This listing has been removed from Cheesta. If that's a mistake, please <Link component={RouterLink} to="/contact">contact us</Link>.
                            </Alert>
                        )}

                        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                    </>
                )}
            </Paper>
        </Container>
    );
};

export default ListingCheckPage;
